/**
 * A rede por baixo do render.
 *
 * O React não tem meio-termo: uma excepção lançada DURANTE o render sobe até à
 * raiz e, se ninguém a apanhar pelo caminho, desmonta a árvore inteira. O que
 * fica é um ecrã branco — sem mensagem, sem botão, sem forma de voltar atrás. E
 * um `try` à volta de um efeito não serve para nada aqui, porque o que rebenta é
 * o próprio render; só uma classe com `getDerivedStateFromError` o apanha.
 *
 * O `initSentry` que já existia não resolve isto: liga o `onerror` e o
 * `onunhandledrejection`, ou seja, RELATA o erro de render e deixa o ecrã branco
 * exactamente na mesma. Relatar e recuperar são coisas diferentes.
 *
 * Só há DUAS fronteiras no cliente inteiro, e cada uma tem uma razão própria:
 *
 * - a da RAIZ (`main.tsx`) é o último recurso. Não sabe recuperar de nada — o
 *   que faz é trocar o ecrã branco por uma frase e um botão de recarregar, que
 *   é a diferença entre "a app avariou" e "o meu computador avariou".
 * - a do ESCRITÓRIO (`AppShell.tsx`) é a que conta no dia-a-dia: 3600 linhas de
 *   canvas, física e Phaser a 60fps, de longe o sítio mais provável para uma
 *   excepção. Sem ela, um erro a desenhar uma árvore levava a conversa, a lista
 *   de membros e os controlos da chamada à frente. Com ela, o resto da app nem
 *   dá por isso e o botão devolve a pessoa ao chat.
 *
 * Não se semeiam mais: uma fronteira só se justifica onde há uma SAÍDA
 * verdadeira para oferecer. Uma que só sabe dizer "rebentou" no meio de uma
 * coluna é ruído a fingir de robustez.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureError } from "../lib/sentry";

type Props = {
  children: ReactNode;
  /**
   * Etiqueta estável do sítio onde isto está montado. Vai para o Sentry e é o
   * que separa "o escritório rebentou" de "a app rebentou" num relatório. É de
   * propósito que NÃO é o título: o título é texto para ler, muda quando se
   * reescreve a frase; a etiqueta tem de sobreviver a essa reescrita.
   */
  zone: string;
  /** O que se lê primeiro. Uma frase, sem jargão. */
  title: string;
  /** O que ainda funciona e o que fazer a seguir. */
  text: string;
  /** Texto do botão principal. Por omissão, "Tentar de novo". */
  retryLabel?: string;
  /**
   * Corre ANTES de limpar o erro. É aqui que a fronteira do escritório manda o
   * `viewMode` de volta para o chat — sem isso, o botão principal montava de
   * novo exactamente a mesma árvore que acabou de rebentar, e o resultado era
   * um botão que pisca e não faz nada.
   */
  onRetry?: () => void;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    // Nem tudo o que se lança é um `Error` — `throw "x"` é JavaScript válido, e
    // o que chega aqui pode vir de uma biblioteca de terceiros.
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // A consola já leva o erro e a pilha de componentes: o React 19 escreve-os
    // sozinho ao entregar a uma fronteira. Repeti-los aqui só dava dois blocos
    // vermelhos iguais. O que a consola do browser de OUTRA pessoa não tem é o
    // Sentry — é só isso que falta fazer.
    captureError(error, this.props.zone, info.componentStack ?? null);
  }

  private readonly retry = (): void => {
    // As duas coisas correm no MESMO tratador de evento, e o React só volta a
    // renderizar depois de ele acabar — por isso o mundo já está mudado quando
    // os filhos montam de novo (e montam de raiz: a sub-árvore que rebentou foi
    // desmontada por inteiro). A ordem escrita aqui é a que se lê melhor; não é
    // ela que segura a garantia.
    this.props.onRetry?.();
    this.setState({ error: null });
  };

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="crash" role="alert">
        <div className="crash__panel">
          <p className="crash__title">{this.props.title}</p>
          <p className="crash__text">{this.props.text}</p>
          <div className="crash__actions">
            <button type="button" className="button button--primary" onClick={this.retry}>
              {this.props.retryLabel ?? "Tentar de novo"}
            </button>
            <button type="button" className="button" onClick={this.reload}>
              Recarregar a página
            </button>
          </div>
          {/* A mensagem crua é a única coisa que quem apanhou isto pode copiar
              para um relatório. Sem DSN de Sentry configurado é, aliás, o único
              vestígio que sobra do erro fora da consola. */}
          {error.message ? <p className="crash__detail">{error.message}</p> : null}
        </div>
      </div>
    );
  }
}
